/**
 * Who gets a "Billing" link.
 *
 * Not `tier !== "free"`. An administrator's tier is granted by role and has no
 * Stripe customer behind it, so that test would draw a button whose only
 * possible outcome is 400 `no_customer` — and an account set to a paid tier
 * from the CLI is the same case. `subscription_status` is the honest signal:
 * it is written by the webhook, so a value means Stripe has heard of this
 * account.
 *
 * A cancelled subscription still qualifies. The portal is where an invoice is
 * downloaded and a subscription is restarted, and taking the link away the
 * moment someone cancels is the one time they most need it.
 *
 * A pure function with tests rather than a condition inline in the header,
 * because "can this person reach Stripe" is a rule and not a rendering detail.
 */

import type { TierId, User } from "../api/types";

/**
 * Values that mean "Stripe has never heard of this account".
 *
 * `none` is not a placeholder we invented here — it is the column default in
 * db/schema.py, so *every* account carries it from the moment it is created.
 * Testing the field for emptiness therefore answered "yes, they have billing"
 * for every user who had never paid, which drew a Billing button that could
 * only ever return `no_customer` and, far worse, made `planActionFor` route a
 * brand-new subscriber to change-plan instead of checkout. Subscribing was
 * impossible: the server correctly answered `no_subscription`, because there
 * was nothing to change.
 *
 * The empty string and null are here too — the column is nullable and rows
 * predating the default exist.
 */
const NEVER_SUBSCRIBED = new Set(["", "none"]);

export function canManageBilling(user: User | null): boolean {
  if (!user) return false;
  return !NEVER_SUBSCRIBED.has((user.subscription_status ?? "").trim().toLowerCase());
}

/**
 * What picking a plan should do.
 *
 * `checkout` sends them to Stripe's hosted page for a new subscription.
 * `change` reprices the one they already have, which costs the difference for
 * the rest of the month rather than a second full charge — so choosing wrong
 * here is choosing to bill someone twice. Same rule the server enforces
 * (409 `change_plan_instead`); this only decides which call to make.
 */
export type PlanAction = "checkout" | "change" | "current";

export function planActionFor(user: User | null, tier: TierId): PlanAction {
  if (!canManageBilling(user)) return "checkout";
  return user!.tier === tier ? "current" : "change";
}

/**
 * Whether the quote carries prorations from a change made earlier in the same
 * period and not yet invoiced.
 *
 * A straightforward switch is two lines: credit for the old plan's unused
 * time, charge for the new plan's remaining time. Stripe adds the still-unbilled
 * lines from any previous change in the period, so a customer on County can
 * find themselves reading "Unused time on State" with no idea why — and a
 * total of $0.00 that looks like a bug rather than like money they already
 * paid. Counting lines is how that is detected; the alternative is matching
 * Stripe's English, which is not ours to depend on.
 */
export function includesEarlierChange(lines: { amount_cents: number }[]): boolean {
  return lines.length > 2;
}

/**
 * A billing date in UTC, matching the "after 06 Aug 2026" that Stripe writes
 * into its own line descriptions.
 *
 * Not the browser's timezone. This anchor is 2026-09-06T00:33:06Z, which is
 * the 5th of September anywhere west of Greenwich — so the default rendering
 * put "your billing date stays September 5" directly underneath four lines
 * dated the 6th. One box cannot use two conventions, and the strings we do not
 * control are the ones to match.
 */
export function formatBillingDate(iso: string | null): string | null {
  if (!iso) return null;
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return null;
  return when.toLocaleDateString(undefined, {
    timeZone: "UTC",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

/**
 * Cents to "$69.98". Two decimals always: "$70" for a charge of $69.98 is the
 * kind of rounding a customer discovers on their statement.
 */
export function formatMoney(cents: number, currency = "usd"): string {
  const symbol = currency.toLowerCase() === "usd" ? "$" : "";
  const sign = cents < 0 ? "-" : "";
  const amount = (Math.abs(cents) / 100).toFixed(2);
  const suffix = symbol ? "" : ` ${currency.toUpperCase()}`;
  return `${sign}${symbol}${amount}${suffix}`;
}

/**
 * The trial, as the plans table says it under the price.
 *
 * "On a first subscription" because that is the rule the server applies and
 * the page cannot see: someone whose earlier subscription lapsed is shown the
 * same table and gets no trial at checkout, and a line promising them one
 * would be the table disagreeing with the card. Empty when there is none.
 *
 * A plan that includes reports says they wait for the first payment: the
 * allowance is zero while trialing (`CreditService.allowance`), and until
 * 28 Sep 2026 only the reminder email three days before the end said so — a
 * trial user expecting the month's reports met the price at the quote.
 */
export function trialLine(days: number, reportCredits = 0): string {
  if (!days || days <= 0) return "";
  const line = `${days} days free on a first subscription`;
  return reportCredits > 0
    ? `${line} · included reports start with the first payment`
    : line;
}

/** The short form, beside a price on the landing page: "7 days free". */
export function trialShort(days: number): string {
  if (!days || days <= 0) return "";
  return `${days} days free`;
}

/**
 * What the switch dialog says when the account is on a free trial.
 *
 * The trial is for one county (the owner's rule, 28 Sep 2026), so moving to
 * another plan ends it and the new plan is paid in full from today — not the
 * difference, and not from the date the trial would have ended. Said plainly
 * because it is the one switch where "you only pay the difference" is untrue.
 */
export function trialEndsOnSwitch(tierName: string): string {
  return (
    `Your free trial is for one county, so it ends now: ${tierName} is ` +
    "charged in full today and renews monthly from today."
  );
}
